/**
 * Supplier Form Modal
 *
 * Modal for creating and editing suppliers.
 */

import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@/lib/zod-resolver';
import { createSupplierSchema, SupplierAccessScope } from '@bstock/shared';
import type { CreateSupplierDto, SupplierWithStats } from '@bstock/shared';
import { Modal, ModalFooter, Button, Input, Select } from '@/components/ui';
import { useCreateSupplier, useUpdateSupplier } from '../hooks/useSuppliers';
import { useBrandContext } from '@/hooks/useBrandContext';

interface SupplierFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplier?: SupplierWithStats | null;
}

type FormData = CreateSupplierDto;

const ACCESS_SCOPE_OPTIONS = [
  { value: SupplierAccessScope.BOTH, label: 'Tous (centrale + restaurants)' },
  { value: SupplierAccessScope.CENTRAL_ONLY, label: 'Centrale uniquement' },
  { value: SupplierAccessScope.SITES_ONLY, label: 'Restaurants uniquement' },
];

export function SupplierFormModal({ isOpen, onClose, supplier }: SupplierFormModalProps) {
  const isEditing = !!supplier;
  const isInternal = supplier?.isInternal ?? false;
  const { isGroup } = useBrandContext();

  const createMutation = useCreateSupplier();
  const updateMutation = useUpdateSupplier();
  const isLoading = createMutation.isPending || updateMutation.isPending;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(createSupplierSchema),
    defaultValues: {
      name: '',
      siret: null,
      code: null,
      email: null,
      phone: null,
      addressLine1: null,
      addressLine2: null,
      postalCode: null,
      city: null,
      country: 'France',
      accessScope: SupplierAccessScope.BOTH,
    },
  });

  // Reset form when supplier changes
  useEffect(() => {
    if (supplier) {
      reset({
        name: supplier.name,
        siret: supplier.siret,
        code: supplier.code,
        email: supplier.email,
        phone: supplier.phone,
        addressLine1: supplier.addressLine1,
        addressLine2: supplier.addressLine2,
        postalCode: supplier.postalCode,
        city: supplier.city,
        country: supplier.country ?? 'France',
        accessScope: supplier.accessScope ?? SupplierAccessScope.BOTH,
      });
    } else {
      reset({
        name: '',
        siret: null,
        code: null,
        email: null,
        phone: null,
        addressLine1: null,
        addressLine2: null,
        postalCode: null,
        city: null,
        country: 'France',
        accessScope: SupplierAccessScope.BOTH,
      });
    }
  }, [supplier, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      if (isEditing && supplier) {
        await updateMutation.mutateAsync({ id: supplier.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
      onClose();
    } catch {
      // Error is handled by mutation
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col max-h-[calc(100vh-10rem)]">
        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {/* Identification */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-neutral-700">Identification</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Nom *"
                {...register('name')}
                error={errors.name?.message}
                placeholder="Nom du fournisseur"
                disabled={isInternal}
                helperText={isInternal ? 'Synchronise avec la centrale' : undefined}
              />
              <Input
                label="Code interne"
                {...register('code')}
                error={errors.code?.message}
                placeholder="Code fournisseur"
              />
            </div>
            <Input
              label="SIRET"
              {...register('siret')}
              error={errors.siret?.message}
              placeholder="14 chiffres"
            />
            {isGroup && (
              <Controller
                name="accessScope"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Visibilite"
                    options={ACCESS_SCOPE_OPTIONS}
                    value={field.value ?? SupplierAccessScope.BOTH}
                    onChange={field.onChange}
                    error={errors.accessScope?.message}
                    disabled={isInternal}
                  />
                )}
              />
            )}
          </div>

          {/* Contact */}
          <div className="space-y-4 pt-4 border-t border-neutral-200">
            <h3 className="text-sm font-medium text-neutral-700">Contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Email"
                type="email"
                {...register('email')}
                error={errors.email?.message}
                placeholder="email@fournisseur.com"
              />
              <Input
                label="Telephone"
                {...register('phone')}
                error={errors.phone?.message}
                placeholder="+33 1 23 45 67 89"
              />
            </div>
          </div>

          {/* Address */}
          <div className="space-y-4 pt-4 border-t border-neutral-200">
            <h3 className="text-sm font-medium text-neutral-700">Adresse</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Adresse"
                {...register('addressLine1')}
                error={errors.addressLine1?.message}
                placeholder="Numero et rue"
              />
              <Input
                label="Complement"
                {...register('addressLine2')}
                error={errors.addressLine2?.message}
                placeholder="Batiment, etage..."
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Input
                label="Code postal"
                {...register('postalCode')}
                error={errors.postalCode?.message}
                placeholder="75001"
              />
              <Input
                label="Ville"
                {...register('city')}
                error={errors.city?.message}
                placeholder="Paris"
              />
              <Input
                label="Pays"
                {...register('country')}
                error={errors.country?.message}
                placeholder="France"
              />
            </div>
          </div>
        </div>

        <ModalFooter className="pt-4 border-t border-neutral-200 mt-4 shrink-0">
          <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button type="submit" isLoading={isLoading}>
            {isEditing ? 'Modifier' : 'Creer'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
